import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import resourcesApi from '../../utils/resourcesApi';
import { checkAdminStatus } from '../../utils/adminUtils';
import ResourceAdminMenu from '../../components/resources/ResourceAdminMenu';
import ResourceCard from '../../components/resources/ResourceCard';
import ResourceSearchInput from '../../components/resources/ResourceSearchInput';
import ResourceConnectionTest from '../../components/resources/ResourceConnectionTest';

const ResourcesPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      console.log('ResourcesPage: Fetching categories...');
      
      const categoriesData = await resourcesApi.getCategories();
      console.log('ResourcesPage: Categories data received:', categoriesData);
      
      // Safety check for empty data
      if (!categoriesData || categoriesData.length === 0) {
        console.log('ResourcesPage: No categories found or empty response');
        setCategories([]);
        return;
      }
      
      // Organize into hierarchy
      const categoriesMap = {};
      const rootCategories = [];
      
      // First pass: create map
      categoriesData.forEach(category => {
        categoriesMap[category.id] = {
          ...category,
          subcategories: []
        };
      });
      
      // Second pass: build hierarchy
      categoriesData.forEach(category => {
        if (category.parent_id) {
          if (categoriesMap[category.parent_id]) {
            categoriesMap[category.parent_id].subcategories.push(categoriesMap[category.id]);
          } else {
            // If parent doesn't exist, add as root
            rootCategories.push(categoriesMap[category.id]);
          }
        } else {
          rootCategories.push(categoriesMap[category.id]);
        }
      });
      
      setCategories(rootCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  }, []);
  
  // Fetch resources with pagination and filtering
  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      console.log('ResourcesPage: Fetching resources...');
      
      // Build query options
      const options = {
        page: currentPage,
        limit: 12, // Show 12 resources per page
      };
      
      if (searchTerm) {
        options.search = searchTerm;
      }
      
      if (currentCategory) {
        options.category = currentCategory;
      }
      
      // Fetch resources from API
      const result = await resourcesApi.getResources(options);
      console.log('ResourcesPage: Resources data received:', result);
      
      // Safety check
      if (!result || !result.data) {
        console.log('ResourcesPage: No resources found or empty response');
        setResources([]);
        setTotalPages(0);
        setLoading(false);
        return;
      }
      
      console.log('ResourcesPage: Processing resources, data array length:', result.data.length);
      
      // Process resources to add missing fields if needed
      const processedResources = result.data.map(resource => {
        // Add debug logging to check what's coming from the API
        console.log('Resource from API:', resource.id, {
          like_count: resource.like_count,
          comment_count: resource.comment_count,
          category_name: resource.category_name || 'Not set',
          resource_categories: resource.resource_categories
        });
        
        // Validate required fields
        if (!resource.id || !resource.title || !resource.slug) {
          console.error('Resource missing required fields:', resource);
        }
        
        return {
          ...resource,
          // Add default values for fields that might be missing from direct Supabase query
          category_name: resource.category_name || (resource.resource_categories ? resource.resource_categories.name : 'General'),
          // Use the actual counts from our API response, or fallback to old property names or 0
          likes_count: resource.like_count || resource.likes_count || 0,
          comments_count: resource.comment_count || resource.comments_count || 0,
          has_audio: resource.has_audio || false,
          is_liked_by_user: resource.is_liked_by_user || false,
        };
      });
      
      console.log('ResourcesPage: Setting resources, processed array length:', processedResources.length);
      // Extra logging to verify object structure
      if (processedResources.length > 0) {
        console.log('First processed resource example:', JSON.stringify(processedResources[0]));
      }
      
      // First set resources and totalPages
      setResources(processedResources);
      setTotalPages(Math.ceil(result.total / result.limit));
      
      // Use a longer timeout to ensure the resource state is updated before loading state changes
      // This prevents the "No resources found" message from appearing between loading and displaying resources
      setTimeout(() => {
        setLoading(false);
      }, 300);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
      setTotalPages(0);
      setLoading(false);
    }
  }, [currentPage, searchTerm, currentCategory]);
  
  // Check if user is admin
  const checkUserAdminStatus = useCallback(async () => {
    if (user) {
      const isUserAdmin = await checkAdminStatus(user);
      setIsAdmin(isUserAdmin);
    } else {
      setIsAdmin(false);
    }
  }, [user]);
  
  useEffect(() => {
    fetchCategories();
    checkUserAdminStatus();
  }, [fetchCategories, checkUserAdminStatus]);
  
  useEffect(() => {
    fetchResources();
  }, [fetchResources]);
  
  // Add this effect to maintain loading state while resources are loading
  useEffect(() => {
    // Don't turn off loading if resources aren't loaded yet
    if (!loading && resources.length === 0) {
      console.log('ResourcesPage: Resources not yet loaded, maintaining loading state');
      setLoading(true);
    }
  }, [loading, resources.length]);
  
  // Handle category click
  const handleCategoryClick = (slug) => {
    setCurrentCategory(slug);
    setCurrentPage(1);
  };
  
  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setCurrentCategory(null);
    setCurrentPage(1);
  };
  
  // Render category menu (recursive)
  const renderCategoryMenu = (categories, level = 0) => {
    return (
      <ul className={`space-y-1 ${level > 0 ? 'ml-4' : ''}`}>
        {categories.map(category => (
          <li key={category.id}>
            <button
              onClick={() => handleCategoryClick(category.slug)}
              className={`block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                currentCategory === category.slug
                  ? 'bg-primary-100 text-primary-800 font-medium'
                  : 'hover:bg-gray-100'
              }`}
            >
              {category.name}
            </button>
            {category.subcategories.length > 0 && renderCategoryMenu(category.subcategories, level + 1)}
          </li>
        ))}
      </ul>
    );
  };
  
  // Add debug logging
  console.log('Resources Page Render State:', {
    resourcesCount: resources.length,
    loading,
    totalPages
  });
  
  return (
    <>
      <ResourceAdminMenu />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Resources</h1>
          <p className="mt-2 text-lg text-gray-600">
            Explore our collection of biotech resources and knowledge base
          </p>
          
          {/* Add prominent Create Resource button for admins */}
          {isAdmin && (
            <div className="mt-4">
              <Link
                to="/resources/new"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Create New Resource
              </Link>
            </div>
          )}
        </div>
        
        {/* Show the test component for debugging */}
        {isAdmin && (
          <div className="mb-8">
            <ResourceConnectionTest />
          </div>
        )}
        
        <div className="flex flex-col md:flex-row">
          {/* Sidebar with categories */}
          <div className="md:w-1/4 lg:w-1/5 mb-8 md:mb-0 md:pr-8">
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold mb-4">Categories</h2>
              
              <button
                onClick={resetFilters}
                className="mb-4 text-primary-600 hover:text-primary-800 text-sm flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset Filters
              </button>
              
              {renderCategoryMenu(categories)}
              
              {/* Admin buttons */}
              {isAdmin && (
                <div className="mt-8 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-500 mb-2">Admin Controls</h3>
                  <Link
                    to="/resources/manage"
                    className="block w-full text-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Manage Resources
                  </Link>
                </div>
              )}
            </div>
          </div>
          
          {/* Main content */}
          <div className="md:w-3/4 lg:w-4/5">
            {/* Search and controls */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <ResourceSearchInput 
                onSearch={(term) => {
                  setSearchTerm(term);
                  setCurrentPage(1);
                }}
                initialValue={searchTerm}
                placeholder="Search resources by title, content, or tags..."
              />
            </div>
            
            {/* Filter indicators */}
            {(currentCategory || searchTerm) && (
              <div className="mb-6 flex flex-wrap gap-2">
                {currentCategory && (
                  <div className="inline-flex items-center bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm">
                    <span>Category: {currentCategory}</span>
                    <button
                      onClick={() => setCurrentCategory(null)}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                {searchTerm && (
                  <div className="inline-flex items-center bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm">
                    <span>Search: {searchTerm}</span>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Resources grid */}
            {console.log('Resources grid render condition:', { resourcesLength: resources.length, loading })}
            {resources.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {console.log('Rendering resource cards:', resources.length)}
                {resources.map(resource => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                  />
                ))}
              </div>
            ) : loading ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                {console.log('Rendering loading state')}
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Loading Resources</h3>
                <p className="text-gray-500">
                  Please wait while we fetch the resources...
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                {console.log('Rendering no resources found')}
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No resources found</h3>
                <p className="text-gray-500">
                  {searchTerm || currentCategory
                    ? "Try different search terms or categories"
                    : "Resources will be added soon. Check back later!"}
                </p>
                {(searchTerm || currentCategory) && (
                  <button
                    onClick={resetFilters}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="inline-flex rounded-md shadow">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-l-md border ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-4 py-2 border-t border-b ${
                        currentPage === page
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-r-md border ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResourcesPage; 